import React, { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { findImportantTerms, matchTermsInText, wikipediaSearchUrl } from '../../services/importantTerms';
import { resolveTermLinks } from '../../services/termLinkApi';
import {                                   // NEW — the shared helper file you already built
  buildSrcDoc,
  buildStandaloneHtm,
  decodeCode,
  downloadFile,
  hydrateEmbeds,
} from './htmlEmbed';                      // ← change the path if your helper file has another name
import '../ImportantTermText/ImportantTermText.css';
import './RichText.css';

/** True when the string contains HTML tags (new rich answers) rather than plain text (old answers). */
export const isHtml = (s) =>
  /<\/?(p|div|span|br|table|thead|tbody|tr|td|th|colgroup|col|ul|ol|li|h[1-6]|strong|em|b|i|u|s|sub|sup|blockquote|mark|a|img)\b[^>]*>/i.test(s || '');

const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Plain text -> paragraphs, HTML -> unchanged. */
export const toHtml = (s) => {
  if (!s) return '';
  return isHtml(s) ? s : s.split(/\n+/).map(l => `<p>${esc(l)}</p>`).join('');
};

/* Links: DOMPurify drops `target`, so set it ourselves after sanitising. Every link opens in a
   new tab with a safe rel, and nobody can sneak in their own target value. */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
});

/** Strips anything unsafe (scripts, event handlers, javascript: links…) but keeps tables, colours,
    fonts, sizes, links and images (including data:image/svg+xml embedded SVGs).
    NEW: keeps the animation placeholders  <div data-html-embed data-code="BASE64">.
    The animation code itself is base64 text here, so the sanitiser never sees (or runs) it —
    it only ever executes later, inside a sandboxed iframe. */
export const cleanHtml = (h) =>
  DOMPurify.sanitize(h || '', {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['data-html-embed', 'data-code'],
  });

const INTERESTED_HEADING = /^for interested students\b/i;
const SECTION_HEADING = /^h[1-6]$/i;

function getEligibleTextNodes(container) {
  const skippedSections = new Set();
  const markers = [...container.querySelectorAll('p, h1, h2, h3, h4, h5, h6')]
    .filter(element => INTERESTED_HEADING.test(element.textContent.trim()));
  markers.forEach(marker => {
    skippedSections.add(marker);
    let sibling = marker.nextElementSibling;
    while (sibling && !SECTION_HEADING.test(sibling.tagName)) {
      skippedSections.add(sibling);
      sibling = sibling.nextElementSibling;
    }
  });

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (parent?.closest('a, code, pre, script, style')) return NodeFilter.FILTER_REJECT;
      if ([...skippedSections].some(section => section.contains(node))) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}

function addImportantTermLinks(html, contextualTerms, useFallback) {
  const container = document.createElement('div');
  container.innerHTML = html;
  getEligibleTextNodes(container).forEach((node) => {
    const fallback = useFallback ? findImportantTerms(node.nodeValue).map(term => ({
      ...term,
      url: wikipediaSearchUrl(term.text),
      sourceLabel: 'Wikipedia search',
    })) : [];
    const contextual = matchTermsInText(node.nodeValue, contextualTerms);
    const matches = matchTermsInText(node.nodeValue, [...contextual, ...fallback]);
    if (!matches.length) return;
    const fragment = document.createDocumentFragment();
    let cursor = 0;
    matches.forEach((match) => {
      if (match.start > cursor) fragment.append(node.nodeValue.slice(cursor, match.start));
      const link = document.createElement('a');
      link.className = 'important-term-link';
      link.href = match.url || wikipediaSearchUrl(match.text);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.title = match.sourceLabel
        ? `Learn about ${match.text} (${match.sourceLabel})`
        : `Find reliable sources about ${match.text}`;
      link.textContent = match.text;
      fragment.append(link);
      cursor = match.end;
    });
    if (cursor < node.nodeValue.length) fragment.append(node.nodeValue.slice(cursor));
    node.parentNode.replaceChild(fragment, node);
  });
  return container.innerHTML;
}

/** True when an editor value has no text and no table / image / animation. */
export const isRichEmpty = (h) =>
  !h || (!/<(table|img|hr)\b|data-html-embed/i.test(h) && h.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, '').length === 0);

/* ═════════════════════════════════════════════════════════════════════════════════════
   NEW — live HTML / SVG animations inside saved answers

   The important-term step below rewrites the answer's HTML after the term lookup returns.
   If the animations lived inside that HTML string, React would throw them away and reload
   them (animation restarts, height jumps) at that moment. So the answer is cut into pieces:
   top-level animations become real React components (they stay mounted and keep playing),
   and the text around them is rendered as before.
   ═════════════════════════════════════════════════════════════════════════════════════ */

/** One animation: sandboxed iframe that fits its content, plus a Download .htm button. */
function EmbedFrame({ code }) {
  const frameRef = useRef(null);
  const [height, setHeight] = useState(240);
  const srcDoc = useMemo(() => buildSrcDoc(code), [code]);

  useEffect(() => {
    const onMsg = (e) => {
      const h = e.data && e.data.htmlEmbedHeight;
      if (typeof h === 'number' && e.source === frameRef.current?.contentWindow) setHeight(Math.max(40, h));
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  return (
    <div style={{ margin: '12px 0' }}>
      <iframe
        ref={frameRef}
        title="Embedded animation"
        sandbox="allow-scripts allow-popups"          /* no allow-same-origin: the code cannot touch your site */
        srcDoc={srcDoc}
        style={{ width: '100%', height, border: 0, display: 'block' }}
      />
      <button
        type="button"
        onClick={() => downloadFile('animation.htm', buildStandaloneHtm(code))}
        style={{ font: '12px system-ui, sans-serif', marginTop: 4, padding: '3px 8px', cursor: 'pointer' }}
      >
        Download .htm
      </button>
    </div>
  );
}

/** Splits sanitised HTML at top-level animation blocks -> [{type:'html',html} | {type:'embed',code}, …] */
function splitEmbeds(html) {
  if (!html || !html.includes('data-html-embed')) return [{ type: 'html', html }];

  const box = document.createElement('div');
  box.innerHTML = html;

  const parts = [];
  let buf = document.createElement('div');
  const flush = () => {
    if (!buf.childNodes.length) return;
    parts.push({ type: 'html', html: buf.innerHTML });
    buf = document.createElement('div');
  };

  [...box.childNodes].forEach((n) => {
    if (n.nodeType === 1 && n.matches('div[data-html-embed]')) {
      const code = decodeCode(n.getAttribute('data-code') || '');
      if (code) { flush(); parts.push({ type: 'embed', code }); return; }
    }
    buf.appendChild(n);
  });
  flush();
  return parts;
}

/** A chunk of answer HTML. An animation nested deeper (inside a table cell, list item, quote…)
    is still a placeholder here, so it is turned into a live iframe once the term lookup has
    settled (hydrating earlier would be wiped by the HTML update and restart it). */
function HtmlBlock({ html, ready, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ready || !html.includes('data-html-embed')) return undefined;
    return hydrateEmbeds(ref.current);
  }, [html, ready]);
  return <div ref={ref} {...rest} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Renders a saved answer. Works for both new HTML answers and old plain-text ones. */
export default function RichContent({ html, className = '' }) {
  const safeHtml = useMemo(() => cleanHtml(toHtml(html)), [html]);
  const [lookup, setLookup] = useState({ status: 'pending', terms: [] });
  useEffect(() => {
    let active = true;
    const container = document.createElement('div');
    container.innerHTML = safeHtml;
    const text = getEligibleTextNodes(container).map(node => node.nodeValue).join('\n');
    setLookup({ status: 'pending', terms: [] });
    if (text.trim().length < 8) {
      setLookup({ status: 'success', terms: [] });
    } else {
      resolveTermLinks(text)
        .then(terms => { if (active) setLookup({ status: 'success', terms }); })
        .catch(error => {
          console.warn('Using Wikipedia term links; contextual lookup failed:', error.message);
          if (active) setLookup({ status: 'fallback', terms: [] });
        });
    }
    return () => { active = false; };
  }, [safeHtml]);
  const clean = useMemo(
    () => addImportantTermLinks(safeHtml, lookup.terms, lookup.status === 'fallback'),
    [safeHtml, lookup],
  );
  const parts = useMemo(() => splitEmbeds(clean), [clean]);   // NEW

  if (!clean) return null;

  const ready = lookup.status !== 'pending';
  const cls = `rt-content rich-content ${className}`;

  // No top-level animation: exactly the same markup as before
  if (parts.length === 1 && parts[0].type === 'html') {
    return <HtmlBlock html={parts[0].html} ready={ready} className={cls} />;
  }

  // Text and animations mixed: animations are stable React children, text chunks are as before
  return (
    <div className={cls}>
      {parts.map((p, i) => (p.type === 'embed'
        ? <EmbedFrame key={`e${i}`} code={p.code} />
        : <HtmlBlock key={`h${i}`} html={p.html} ready={ready} style={{ display: 'contents' }} />))}
    </div>
  );
}